import { useState, useMemo, useCallback } from 'react';

import { useMapLocations } from '@/features/map-locations';
import { useMapCorridors } from '@/features/map-corridors';
import { useTimeMachine } from '@/features/map-time-machine';
import { useDataLayer } from '@/features/map-data-layers';

import {
  useGetMappingStockQuery,
  useGetMappingReleasesQuery,
  useGetMappingRequestsQuery
} from '@/entities/dashboard/mapping';
import type { LocationModel } from '@/entities/location';

import { LocationTypeFilter, FilteredLocationCounts, LOCATION, Location } from '@/pages/dashboard/mapping/model/types';
import { useProjectSiteParams } from '@/shared/lib';

import { extractDataValue } from './utils';

interface UseMappingDashboardProps {
  organizationId?: string;
}

export function useMappingDashboard({ organizationId }: UseMappingDashboardProps = {}) {
  const { projectId, siteId } = useProjectSiteParams();
  const [selectedLocation, setSelectedLocation] = useState<LocationModel | null>(null);
  const [showCorridors, setShowCorridors] = useState(true);
  const [showTimeMachine, setShowTimeMachine] = useState(false);
  const [locationTypeFilter, setLocationTypeFilter] = useState<LocationTypeFilter>({
    [LOCATION.W]: true,
    [LOCATION.P]: true,
    [LOCATION.C]: true,
    [LOCATION.S]: true
  });

  const {
    locations,
    statistics: locationStats,
    isLoading: locationsLoading,
    error: locationsError
  } = useMapLocations();

  const timeMachine = useTimeMachine({ minWeek: -52, maxWeek: 0 });
  const weekNo = showTimeMachine && timeMachine.currentWeek !== 0 ? Math.abs(timeMachine.currentWeek) : undefined;

  const dataLayer = useDataLayer('stock');

  const queryParams = useMemo(
    () => ({
      weekNo,
      projectId,
      siteId,
      ...(organizationId && { organizationId })
    }),
    [weekNo, projectId, siteId, organizationId]
  );

  const {
    corridorDisplays,
    corridorType,
    setCorridorType,
    statistics: corridorStats
  } = useMapCorridors(locations, weekNo, organizationId);

  const { data: stockData, isFetching: isStockFetching } = useGetMappingStockQuery(queryParams, {
    skip: dataLayer.activeLayer !== 'stock',
    refetchOnMountOrArgChange: false
  });

  const { data: releasesData, isFetching: isReleasesFetching } = useGetMappingReleasesQuery(queryParams, {
    skip: dataLayer.activeLayer !== 'releases',
    refetchOnMountOrArgChange: false
  });

  const { data: requestsData, isFetching: isRequestsFetching } = useGetMappingRequestsQuery(
    { type: 'RIC', ...queryParams },
    {
      skip: dataLayer.activeLayer !== 'requests',
      refetchOnMountOrArgChange: false
    }
  );

  const activeLayerData = useMemo(() => {
    switch (dataLayer.activeLayer) {
      case 'stock':
        return stockData?.features || [];
      case 'releases':
        return releasesData?.features || [];
      case 'requests':
        return requestsData?.features || [];
      default:
        return [];
    }
  }, [dataLayer.activeLayer, stockData, releasesData, requestsData]);

  const locationsWithData = useMemo(() => {
    const dataMap = new Map(activeLayerData.map((f) => [f.id, f]));

    return locations.map((loc) => {
      const data = dataMap.get(loc.id);
      const dataValue = extractDataValue(data, showTimeMachine, weekNo);

      return Object.assign(loc, {
        dataValue,
        dataProgress: data?.progress || []
      });
    });
  }, [locations, activeLayerData, showTimeMachine, weekNo]);

  const filteredLocations = useMemo(() => {
    return locationsWithData.filter((loc) => locationTypeFilter[loc.type as keyof LocationTypeFilter]);
  }, [locationsWithData, locationTypeFilter]);

  const filteredLocationCounts: FilteredLocationCounts = useMemo(() => {
    const counts = { [LOCATION.W]: 0, [LOCATION.P]: 0, [LOCATION.C]: 0, [LOCATION.S]: 0 };

    filteredLocations.forEach((loc) => {
      const key = LOCATION[loc.type] as Location;
      counts[key] += 1;
    });

    return counts;
  }, [filteredLocations]);

  const handleLocationTypeToggle = useCallback((type: keyof LocationTypeFilter) => {
    setLocationTypeFilter((prev) => ({
      ...prev,
      [type]: !prev[type]
    }));
  }, []);

  const toggleAllLocationTypes = useCallback(() => {
    const allSelected = Object.values(locationTypeFilter).every((v) => v);
    setLocationTypeFilter({
      [LOCATION.W]: !allSelected,
      [LOCATION.P]: !allSelected,
      [LOCATION.C]: !allSelected,
      [LOCATION.S]: !allSelected
    });
  }, [locationTypeFilter]);

  // Loading states
  const isDataFetching = isStockFetching || isReleasesFetching || isRequestsFetching;
  const isLoading = locationsLoading || isDataFetching;

  return {
    // Data
    locations: filteredLocations,
    corridors: corridorDisplays,
    locationStats,
    corridorStats,
    filteredLocationCounts,

    // State
    selectedLocation,
    showCorridors,
    showTimeMachine,
    locationTypeFilter,
    corridorType,
    dataLayer: dataLayer.activeLayer,
    timeMachine: {
      ...timeMachine,
      weekNo,
      isActive: showTimeMachine
    },

    // Actions
    setSelectedLocation,
    setShowCorridors,
    setShowTimeMachine,
    setCorridorType,
    setDataLayer: dataLayer.setLayer,
    handleLocationTypeToggle,
    toggleAllLocationTypes,

    // Loading states
    isLoading,
    isDataFetching,
    error: locationsError
  };
}
